import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  useAddCartMutation,
  useEditCartItemMutation,
  useGetPickupPointsQuery,
} from '../../apis/mlkApi/mlk.api.hooks';
import { parseRes } from '../../apis/utils';
import { Button } from '../../components/Button/Button';
import { EmptyState } from '../../components/EmptyState/EmptyState';
import { EmptyStateType } from '../../components/EmptyState/emptyStateHelper';
import Layout, { OrderRequestResult } from '../../components/Layout/Layout';
import {
  ComposedMaps,
  LatLng,
  markerIcons,
  MarkerType,
} from '../../components/Map/Map';
import { ModalRef } from '../../components/Modal/Modal';
import { StrongRegistrationWrap } from '../../components/StrongRegistrationWrap/StrongRegistrationWrap';
import {
  getIsEmailConfirmed,
  setupPickUpPointStrongSignInOperation,
} from '../../helpers/auth';
import { useBack } from '../../hooks/useBack';
import { useLanguage } from '../../locale/useLanguage';
import { FaqFeature, OperationType } from '../../models';
import { Product } from '../../models';
import { PdrCartElement } from '../../models/payment.models';
import { PickUpPoint } from '../../models/pickupPoints.models';
import { setOperation } from '../../redux/general/general.actions';
import { getOperationSelector } from '../../redux/general/general.selectors';
import { authTokenSelector } from '../../redux/user/user.selectors';
import { Routes } from '../../routes/types';
import { PickUpElement } from './components/PickUpElement/PickUpElement';
import { WeightWarningModal } from './components/WeightWarningModal/WeightWarningModal';

import './SelectPickUpPointView.css';

type SelectPickUpPointRouteState =
  | {
      pdrCartItem: PdrCartElement;
    }
  | undefined;

type SelectPickUpPointProps = {};

export const SelectPickUpPoint: React.FC<SelectPickUpPointProps> = () => {
  const [t] = useLanguage();
  const goBack = useBack();
  const authToken = useSelector(authTokenSelector);
  const history = useHistory();
  const location = useLocation<SelectPickUpPointRouteState>();
  const dispatch = useDispatch();
  const [addToCart] = useAddCartMutation();
  const [editCartItemApiCall] = useEditCartItemMutation();
  const weightWarningModalRef = useRef<ModalRef>(null);

  const operation = useSelector(getOperationSelector);
  const [orderResponse, setOrderResponse] = useState<OrderRequestResult>();
  const [selectedPDR, setSelectedPDR] = useState<PickUpPoint | undefined>(
    operation?.savedData.selectedPdr
  );

  const isEmailConfirmed = useMemo(() => {
    if (authToken) {
      return getIsEmailConfirmed(authToken);
    }

    return null;
  }, [authToken]);

  useEffect(() => {
    if ((!authToken || !isEmailConfirmed) && !operation) {
      setupPickUpPointStrongSignInOperation(dispatch);
    }
  }, [authToken, dispatch, isEmailConfirmed, operation]);

  const { data: pdrs } = useGetPickupPointsQuery(
    orderResponse?.data?.id ? `${orderResponse?.data?.id}` : '',
    {
      skip: orderResponse?.data?.id === undefined,
    }
  );

  useEffect(() => {
    if (pdrs && location.state?.pdrCartItem) {
      const pdrToEdit = pdrs.find(
        (p) => p.id === location.state?.pdrCartItem.configuration.pdrId
      );
      setSelectedPDR(pdrToEdit);
    }
  }, [location.state?.pdrCartItem, pdrs]);

  const markers: MarkerType[] | undefined = useMemo(() => {
    let prdMarker = pdrs?.map((pp) => {
      const isSelected =
        selectedPDR?.lat === pp.lat && selectedPDR.lng === pp.lng;

      return {
        lat: +pp.lat,
        lng: +pp.lng,
        icon: isSelected ? markerIcons.baseSelected : markerIcons.base,
      };
    });
    if (orderResponse?.data?.deliveryAddress)
      prdMarker?.push({
        lat: +orderResponse?.data?.deliveryAddress.lat,
        lng: +orderResponse?.data?.deliveryAddress.lon,
        icon: markerIcons.home,
      });

    return prdMarker;
  }, [pdrs, selectedPDR?.lat, selectedPDR?.lng, orderResponse?.data]);

  const selectPdr = useCallback(
    (pickUpPoint: PickUpPoint | undefined) => {
      if (operation && operation.routing.url === Routes.SelectPickUpPoint) {
        dispatch(
          setOperation({
            ...operation,
            savedData: {
              selectedPdr: pickUpPoint,
            },
          })
        );
      }
      setSelectedPDR(pickUpPoint);
    },
    [dispatch, operation]
  );

  const selectPdrOnMarkerPress = useCallback(
    (latLng: LatLng | undefined) => {
      const pdrToSelect = pdrs?.find(
        (p) => +p.lat === latLng?.lat && +p.lng === latLng?.lng
      );
      selectPdr(pdrToSelect);
    },
    [pdrs, selectPdr]
  );

  const editPdrInCart = useCallback(() => {
    if (!location.state) return;
    editCartItemApiCall({
      cartElement: {
        id: location.state.pdrCartItem.id,
        product: location.state.pdrCartItem.product,
        orderId: location.state.pdrCartItem.orderId,
        configuration: {
          pdrId: selectedPDR!.id,
        },
      },
    }).then((res) => {
      const { successRes } = parseRes(res);
      if (successRes) {
        history.push(Routes.Cart);
      }
    });
  }, [editCartItemApiCall, history, location.state, selectedPDR]);

  const addPdrToCart = useCallback(() => {
    addToCart({
      product: Product.Pdr,
      configuration: {
        pdrId: selectedPDR!.id,
      },
      orderId: orderResponse!.data!.id,
    }).then((res) => {
      const { successRes } = parseRes(res);
      if (successRes.data) {
        history.replace(Routes.Cart);
      }
    });
  }, [addToCart, history, orderResponse, selectedPDR]);

  const continueToCart = useCallback(() => {
    weightWarningModalRef.current?.closeModal();
    if (location.state?.pdrCartItem) {
      editPdrInCart();
    } else {
      addPdrToCart();
    }
  }, [addPdrToCart, editPdrInCart, location.state?.pdrCartItem]);

  const goToInfo = useCallback(() => {
    history.push(Routes.Faq.replace(':feature', FaqFeature.Pdr));
  }, [history]);

  const openWeightWarningModal = useCallback(() => {
    weightWarningModalRef.current?.openModal();
  }, []);

  return (
    <>
      <Layout
        hasBack
        onBackClick={() => {
          dispatch(setOperation(null));
          goBack();
        }}
        onOrderChange={setOrderResponse}
        hasViewportHeight
        hasInfo
        onInfoClick={goToInfo}
        title={t('pageTitles.pickUp')}
      >
        {pdrs && (
          <>
            <h2 className="pick-up-points--heading">
              {t('pickUp.pickUpPointsNear')}
            </h2>
            <p>{orderResponse?.data?.deliveryAddress.address}</p>
            <div className="pick-up-points--map-container">
              <ComposedMaps
                mapHeight="200px"
                onMarkerClick={selectPdrOnMarkerPress}
                zoom={14}
                isGestureEnabled={true}
                centerMap={{
                  lat: orderResponse!.data!.deliveryAddress.lat,
                  lng: orderResponse!.data!.deliveryAddress.lon,
                }}
                markers={markers}
              />
            </div>
            <div className="pick-up-points--options-container">
              {pdrs.length > 0 ? (
                [...pdrs]
                  .sort((a, b) => {
                    if (a.distance < b.distance) {
                      return -1;
                    }
                    if (a.distance > b.distance) {
                      return 1;
                    }
                    return 0;
                  })
                  .map((p) => {
                    const isSelected =
                      selectedPDR?.lat === p.lat && selectedPDR.lng === p.lng;
                    return (
                      <PickUpElement
                        onElementSelect={selectPdr}
                        key={p.id}
                        isSelected={isSelected}
                        element={p}
                      />
                    );
                  })
              ) : (
                <EmptyState type={EmptyStateType.PickUpPoint} cta={null} />
              )}
            </div>
            {selectedPDR && (
              <div className="pick-up-points--confirm">
                <StrongRegistrationWrap
                  operationType={OperationType.Pdr}
                  successAction={openWeightWarningModal}
                >
                  <Button isPrimary label={t('pickUp.continue')} />
                </StrongRegistrationWrap>
              </div>
            )}
          </>
        )}
      </Layout>
      <WeightWarningModal
        ref={weightWarningModalRef}
        onContinue={continueToCart}
      />
    </>
  );
};
